import { useQuery } from '@tanstack/react-query';

import { passesApi } from './passes-api';
import { passKeys } from './query-keys';
import { useBeckonDraft } from './use-beckon-draft';

/**
 * Polls the entry-pass booking draft for a transaction — a thin wrapper over the
 * generic useBeckonDraft (shared with metro + rides). The draft drives the whole
 * wizard: on_search lands venues + passes, on_select/on_init land the quote (and
 * any xinput form), on_confirm lands the orderId. Stops when the current step
 * completes/errors; flags timedOut after ~30s per step.
 */
export function usePassDraft(transactionId: string | null) {
  return useBeckonDraft(transactionId, passesApi.draft, passKeys.draft);
}

/** The user's purchased entry passes (confirmed orders). */
export function usePassList() {
  return useQuery({
    queryKey: passKeys.list(),
    queryFn: () => passesApi.list(),
  });
}

/**
 * A single purchased entry pass. The QR is static, but we keep polling while a
 * credential is still UNCLAIMED (to flip to CLAIMED once scanned at the venue) and
 * fast (1.5s) during the 2-stage cancel handshake so the SOFT/CONFIRM callbacks land.
 */
export function usePassDetail(id: string, fast = false) {
  const query = useQuery({
    queryKey: passKeys.detail(id),
    queryFn: () => passesApi.pass(id),
    refetchInterval: (q) => {
      if (fast) return 1500;
      const t = q.state.data;
      if (!t) return 5000;
      if (t.status === 'SOFT_CANCEL') return 2000; // cancel preview in flight
      const pending = (t.passes ?? []).some((p) => p.ticketStatus === 'UNCLAIMED');
      return pending && t.status === 'ACTIVE' ? 5000 : false;
    },
  });

  return {
    ticket: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
